import isInteger from "@homer.utility/validator.isinteger";
import isString from "@homer.utility/validator.isstring";
import isNumeric from "@homer.utility/validator.isnumeric";
import isArray from "@homer.utility/validator.isarray";
import isObject from "@homer.utility/validator.isobject";
import isUndefined from "@homer.utility/validator.isundefined";

const validate = {
    float: (value) => {
        return parseFloat(value.toFixed(2));
    },
    fields: (fields = []) => {
        const failed = [];

        fields.forEach(({ name, value, validator }) => {
            Object.entries(validator).forEach(([id, validator]) => {
                if (!validator(value)) {
                    failed.push(id);
                }
            });

            if (failed.length) {
                throw new TypeError(`argument \`details.${name}\` did not pass validator ${failed.join(", ")}`);
            }
        });
    },
    item: (item = {}, additional = []) => {
        const { id, sku, name, alternateName, brand, price, category, segment, position, slot, quantity, total } = item || {};
        const validator = [
            { name: "items[].id", value: id, validator: { isInteger } },
            { name: "items[].sku", value: sku, validator: { isString } },
            { name: "items[].name", value: name, validator: { isString } },
            { name: "items[].alternateName", value: alternateName, validator: { isString } },
            { name: "items[].brand", value: brand, validator: { isString } },
            { name: "items[].price", value: price, validator: { isNumeric } },
        ];

        if (!isUndefined(category)) {
            validator.push({ name: "items[].category", value: category, validator: { isString } });
        }

        if (!isUndefined(segment)) {
            validator.push({ name: "items[].segment", value: segment, validator: { isObject } });
        }

        if (!isUndefined(position)) {
            validator.push({ name: "items[].position", value: position, validator: { isInteger } });
        }

        if (!isUndefined(slot)) {
            validator.push({ name: "items[].slot", value: slot, validator: { isString } });
        }

        if (additional.indexOf("quantity") !== -1) {
            validator.push({ name: "items[].quantity", value: quantity, validator: { isInteger } });
        }

        if (additional.indexOf("total") !== -1) {
            validator.push({ name: "items[].total", value: total, validator: { isNumeric } });
        }

        validate.fields(validator);

        return {
            id,
            sku: sku.trim(),
            name: name.trim(),
            alternateName: alternateName.trim(),
            brand: brand.trim(),
            category: category ? category.trim() : undefined,
            segment: segment || undefined,
            price: validate.float(price),
            position: !isUndefined(position) ? position : undefined,
            slot: !isUndefined(slot) ? slot : undefined,
            quantity: additional.indexOf("quantity") !== -1 ? quantity : undefined,
            total: additional.indexOf("total") !== -1 ? validate.float(total) : undefined,
        };
    },
    items: (items = [], additional = []) => {
        validate.fields([{ name: "items", value: items, validator: { isArray } }]);

        return items.map((item) => validate.item(item, additional));
    },
    voucher: (voucher = {}) => {
        const { code, issuer } = voucher || {};

        validate.fields([
            { name: "voucher[].code", value: code, validator: { isString } },
            { name: "voucher[].issuer", value: issuer, validator: { isString } },
        ]);

        return {
            code,
            issuer,
        };
    },
    vouchers: (vouchers = []) => {
        if (isArray(vouchers)) {
            return vouchers.map((voucher) => validate.voucher(voucher));
        }
    },
    search: (search) => {
        const { index, query, source } = search || {};
        const validator = [{ name: "search.index", value: index, validator: { isString } }];

        if (query) {
            validator.push({ name: "search.query", value: query, validator: { isString } });
        }

        if (source) {
            validator.push({ name: "search.source", value: source, validator: { isString } });
        }

        validate.fields(validator);

        return { index, query, source };
    },
    list: (list) => {
        const { id, name } = list || {};

        if (list) {
            const validator = [];

            validator.push({ name: "list", value: list, validator: { isObject } });
            validator.push({ name: "list.id", value: id, validator: { isString } });
            validator.push({ name: "list.name", value: name, validator: { isString } });

            validate.fields(validator);
        }

        return { id, name };
    },
};

export default validate;
